// import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
// import CardActions from "@material-ui/core/CardActions";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import CardMenu from "@saleor/components/CardMenu";
import CardTitle from "@saleor/components/CardTitle";
import Money from "@saleor/components/Money";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellAvatar, {
  AVATAR_MARGIN
} from "@saleor/components/TableCellAvatar";
import { ShipmentByTrackingNumber_shipmentByTrackingNumber } from "@saleor/orders/types/ShipmentByTrackingNumber";
import classNames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { getStringOrPlaceholder, maybe, renderCollection } from "../../../misc";
import { FulfillmentStatus } from "../../../types/globalTypes";
import {
  OrderDetails_order_fulfillments,
  OrderDetails_order_sendgridWebhook_edges_node
} from "../../types/OrderDetails";

const useStyles = makeStyles(
  theme => ({
    root: {
      lineHeight: 1.9,
      width: "100%"
    },
    infotime: {
      fontSize: 10,
      width: 150
    },
    infodetail: {
      fontSize: 10
    },
    infostatus: {
      fontSize: 10
    },
    clickableRow: {
      cursor: "pointer"
    },
    colName: {
      width: "auto"
    },
    colImg: {
      width: 120
    },
    colNameLabel: {
      marginLeft: AVATAR_MARGIN
    },
    colPrice: {
      textAlign: "right",
      width: 120
    },
    colQuantity: {
      textAlign: "center",
      width: 120
    },
    colSku: {
      textAlign: "right",
      textOverflow: "ellipsis",
      width: 120
    },
    colTotal: {
      textAlign: "right",
      width: 120
    },
    infoLabel: {
      display: "inline-block"
    },
    infoLabelWithMargin: {
      marginBottom: theme.spacing()
    },
    infoRow: {
      padding: theme.spacing(2, 3)
    },
    orderNumber: {
      display: "inline",
      marginLeft: theme.spacing(1)
    },
    statusBar: {
      paddingTop: 0
    },
    table: {
      tableLayout: "fixed"
    }
  }),
  { name: "OrderFulfillment" }
);

interface OrderFulfillmentProps {
  fulfillment: OrderDetails_order_fulfillments;
  orderNumber: string;
  onOrderFulfillmentCancel: () => void;
  onTrackingCodeAdd: () => void;
  shipmentInfos: ShipmentByTrackingNumber_shipmentByTrackingNumber[];
  webhookInfos: OrderDetails_order_sendgridWebhook_edges_node[];
}

const numberOfColumns = 8;

const OrderFulfillment: React.FC<OrderFulfillmentProps> = props => {
  const {
    fulfillment,
    orderNumber,
    onOrderFulfillmentCancel,
    // onTrackingCodeAdd,
    shipmentInfos
    // webhookInfos
  } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  const lines = fulfillment?.lines;
  const status = maybe(() => fulfillment.status);
  const quantity = lines
    ? lines.map(line => line.quantity).reduce((prev, curr) => prev + curr, 0)
    : "...";
  const strSPH = "SPH";
  const strCYL = "CYL";
  const strAXIS = "AXIS";
  const strADD = "ADD";
  return (
    <Card>
      <CardTitle
        title={
          !!lines ? (
            <StatusLabel
              label={
                <>
                  {status === FulfillmentStatus.FULFILLED
                    ? intl.formatMessage(
                        {
                          defaultMessage: "Fulfilled ({quantity})",
                          description: "section header"
                        },
                        {
                          quantity
                        }
                      )
                    : intl.formatMessage(
                        {
                          defaultMessage: "Cancelled ({quantity})",
                          description: "cancelled fulfillment, section header"
                        },
                        {
                          quantity
                        }
                      )}
                  <Typography className={classes.orderNumber} variant="body1">
                    {maybe(
                      () => `#${orderNumber}-${fulfillment.fulfillmentOrder}`
                    )}
                  </Typography>
                </>
              }
              status={
                status === FulfillmentStatus.FULFILLED ? "success" : "error"
              }
            />
          ) : (
            <Skeleton />
          )
        }
        toolbar={
          maybe(() => fulfillment.status) === FulfillmentStatus.FULFILLED && (
            <CardMenu
              menuItems={[
                {
                  label: intl.formatMessage({
                    defaultMessage: "Cancel Fulfillment",
                    description: "button"
                  }),
                  onSelect: onOrderFulfillmentCancel
                }
              ]}
            />
          )
        }
      />
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.colName} colSpan={4}>
              <span className={classes.colNameLabel}>
                <FormattedMessage
                  defaultMessage="Product"
                  description="product name"
                />
              </span>
            </TableCell>
            <TableCell className={classes.colSku}>
              <FormattedMessage
                defaultMessage="SKU"
                description="ordered product sku"
              />
            </TableCell>
            <TableCell className={classes.colQuantity}>
              <FormattedMessage
                defaultMessage="Quantity"
                description="ordered product quantity"
              />
            </TableCell>
            <TableCell className={classes.colPrice}>
              <FormattedMessage
                defaultMessage="Price"
                description="product price"
              />
            </TableCell>
            <TableCell className={classes.colTotal}>
              <FormattedMessage
                defaultMessage="Total"
                description="order line total price"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderCollection(lines, line => {
            const customizedData = line?.orderLine?.customizedData.replaceAll(
              ": null",
              ': "None"'
            );
            return (
              <>
                <TableRow
                  className={!!line ? classes.clickableRow : undefined}
                  hover={!!line}
                  key={maybe(() => line.id)}
                >
                  <TableCellAvatar
                    className={classes.colName}
                    thumbnail={maybe(() => line?.orderLine?.thumbnail.url)}
                    colSpan={4}
                  >
                    {line ? (
                      maybe(
                        () =>
                          line?.orderLine?.productName +
                          "  " +
                          line?.orderLine?.variantName
                      )
                    ) : (
                      <Skeleton />
                    )}
                  </TableCellAvatar>
                  <TableCell className={classes.colSku}>
                    {line?.orderLine.productSku || <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.colQuantity}>
                    {line?.quantity || <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.colPrice}>
                    {maybe(() => line?.orderLine?.unitPrice.net) ? (
                      <Money money={line?.orderLine?.unitPrice.net} />
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colTotal}>
                    {maybe(
                      () =>
                        line?.quantity * line?.orderLine?.unitPrice.net.amount
                    ) ? (
                      <Money
                        money={{
                          amount:
                            line.quantity *
                            line.orderLine?.unitPrice.net.amount,
                          currency: line.orderLine?.unitPrice.net.currency
                        }}
                      />
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
                {line?.orderLine &&
                  (customizedData !== "{}" ||
                    line?.orderLine?.extrachargeData !== "{}") && (
                    <TableRow>
                      {/* 个人定制 */}
                      <TableCell colSpan={5}>
                        {customizedData &&
                          JSON.stringify(customizedData) !== "{}" && (
                            <table className={classes.root}>
                              {Object.keys(JSON.parse(customizedData))?.length >
                                2 && (
                                <tr>
                                  <td>{""}</td>
                                  <td>{strSPH}</td>
                                  <td>{strCYL}</td>
                                  <td>{strAXIS}</td>
                                  <td>{strADD}</td>
                                </tr>
                              )}
                              {Object.keys(JSON.parse(customizedData)).map(
                                key => (
                                  <tr>
                                    <td>{key}</td>
                                    {JSON.parse(customizedData)[key].length >
                                      0 && (
                                      <>
                                        {key === "Upload a photo" ? (
                                          <td>
                                            <img
                                              className={classes.colImg}
                                              src={
                                                JSON.parse(customizedData)[key]
                                              }
                                            />
                                          </td>
                                        ) : (
                                          <td>
                                            <span>
                                              {JSON.parse(customizedData)[key]}
                                            </span>
                                          </td>
                                        )}
                                      </>
                                    )}
                                    {Object.keys(JSON.parse(customizedData))
                                      ?.length > 2 && (
                                      <>
                                        <td>
                                          {
                                            JSON.parse(customizedData)[key][
                                              strSPH
                                            ]
                                          }
                                        </td>
                                        <td>
                                          {
                                            JSON.parse(customizedData)[key][
                                              strCYL
                                            ]
                                          }
                                        </td>
                                        <td>
                                          {
                                            JSON.parse(customizedData)[key][
                                              strAXIS
                                            ]
                                          }
                                        </td>
                                        <td>
                                          {
                                            JSON.parse(customizedData)[key][
                                              strADD
                                            ]
                                          }
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                )
                              )}
                              {line?.orderLine.optometryImage && (
                                <tr>
                                  <td>{"Optometry Image"}</td>
                                  <td>
                                    <img
                                      className={classes.colImg}
                                      src={line?.orderLine.optometryImage}
                                    />
                                  </td>
                                </tr>
                              )}
                            </table>
                          )}
                      </TableCell>

                      {/* 定制收费 */}
                      <TableCell colSpan={3}>
                        {line?.orderLine?.extrachargeData &&
                          JSON.stringify(line?.orderLine?.extrachargeData) !==
                            "{}" && (
                            <table className={classes.root}>
                              {Object.keys(
                                JSON.parse(line.orderLine.extrachargeData)
                              ).map(key => (
                                <>
                                  <tr>
                                    <td> {key} </td>
                                    <td>
                                      <Money
                                        money={{
                                          amount: JSON.parse(
                                            line.orderLine.extrachargeData
                                          )[key].price,
                                          currency: JSON.parse(
                                            line.orderLine.extrachargeData
                                          )[key].currency
                                        }}
                                      />
                                    </td>
                                  </tr>
                                </>
                              ))}
                            </table>
                          )}
                      </TableCell>
                    </TableRow>
                  )}
              </>
            );
          })}
          {false && (
            <>
              <TableRow>
                <TableCell
                  className={classes.infoRow}
                  colSpan={numberOfColumns}
                >
                  <Typography color="textSecondary" variant="body2">
                    <FormattedMessage
                      defaultMessage="Fulfilled from: {warehouseName}"
                      description="fulfillment group"
                      values={{
                        warehouseName: (
                          <Typography
                            className={classNames(classes.infoLabel, {
                              [classes.infoLabelWithMargin]: !!fulfillment?.trackingNumber
                            })}
                            color="textPrimary"
                            variant="body2"
                          >
                            {getStringOrPlaceholder(
                              fulfillment?.warehouse?.name
                            )}
                          </Typography>
                        )
                      }}
                    />
                  </Typography>
                </TableCell>
              </TableRow>
              {fulfillment?.trackingNumber &&
                renderCollection(shipmentInfos, shipmentInfo => {
                  const [open, setOpen] = React.useState(false);
                  return (
                    <TableRow>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => setOpen(!open)}
                        >
                          {open ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell colSpan={8}>
                        <FormattedMessage
                          defaultMessage="Tracking Number: {trackingNumber}"
                          values={{
                            trackingNumber: (
                              <Typography
                                className={classes.infoLabel}
                                color="textPrimary"
                                variant="body2"
                              >
                                {shipmentInfo?.trackingNumber}
                              </Typography>
                            )
                          }}
                        />
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box margin={1}>
                                {shipmentInfos &&
                                  renderCollection(
                                    shipmentInfo.shipmentInfos,
                                    info => (
                                      <TableRow>
                                        <TableCell className={classes.infotime}>
                                          {info?.time}
                                        </TableCell>
                                        <TableCell
                                          className={classes.infodetail}
                                        >
                                          {info?.detail}
                                        </TableCell>
                                        <TableCell
                                          className={classes.infostatus}
                                        >
                                          {info?.status +
                                            ":" +
                                            info?.statusDescription}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </>
          )}
        </TableBody>
      </ResponsiveTable>
      {/* {status === FulfillmentStatus.FULFILLED && !fulfillment.trackingNumber && (
        <CardActions>
          <Button color="primary" onClick={onTrackingCodeAdd}>
            <FormattedMessage
              defaultMessage="Add tracking"
              description="fulfillment group tracking number"
            />
          </Button>
        </CardActions>
      )}
      {status === FulfillmentStatus.FULFILLED && fulfillment.trackingNumber && (
        <CardActions>
          <Button color="primary" onClick={onTrackingCodeAdd}>
            <FormattedMessage
              defaultMessage="Edit tracking"
              description="fulfillment group tracking number"
            />
          </Button>
        </CardActions>
      )} */}
    </Card>
  );
};
OrderFulfillment.displayName = "OrderFulfillment";
export default OrderFulfillment;
